import type { ApiClient } from "@majourneys/api-client";
import { unwrap } from "@majourneys/api-client";
import type { TransferBookingInput } from "@majourneys/shared";
import { landingGclid, readAdCampaign, readMetaAttribution, readReferral, readAiAssistant, readTrafficSource } from "../lib/attribution";

/**
 * The current site's hostname, sent as the booking `source` so the admin can
 * attribute a reservation to the microsite it came from. The API allowlists
 * known network domains and maps the main hub / unknowns to "web", so sending
 * the raw hostname is safe.
 */
function deriveSource(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname.replace(/^www\./, "");
  if (!host || host === "localhost") return undefined;
  return host;
}

/**
 * Google Ads click id for this booking, read from the `_gcl_aw` cookie that
 * gtag writes when a visitor lands on the site from an ad. Returned undefined
 * when the visitor didn't come from Google Ads.
 *
 * Why the cookie and not `?gclid=` in the URL: the router strips the query
 * string almost immediately and checkout is several pages further on, so the
 * parameter is long gone by the time we submit. The cookie also survives the
 * whole 90-day attribution window, so it still credits a visitor who clicked
 * the ad days ago and returned directly — which the URL never could.
 *
 * Cookie format is `GCL.<timestamp>.<clickId>`; we keep only the click id.
 * A malformed value yields undefined rather than storing junk.
 *
 * With no cookie, falls back to the click id held in memory from the landing
 * URL, which is released only once the visitor has accepted ad cookies: gtag
 * writes no cookie under Consent Mode, so a late "Accept" would otherwise lose
 * the click. A visitor who declined keeps no click id on the booking.
 */
export function readGclid(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const raw = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("_gcl_aw="));
  if (!raw) return landingGclid();
  const value = decodeURIComponent(raw.slice("_gcl_aw=".length));
  // `GCL.1712345678.CjwKCAjw...` — the click id is everything after the second dot,
  // and click ids themselves can contain dots, so don't split-and-take-index-2.
  const match = /^GCL\.\d+\.(.+)$/.exec(value);
  const clickId = match?.[1]?.trim();
  if (!clickId) return undefined;
  // Guard the API's max length here too, so an oversized cookie fails the
  // booking's attribution rather than the booking itself.
  return clickId.length <= 512 ? clickId : undefined;
}

export interface CheckoutPayload {
  activityId: string;
  activitySlug: string;
  /** Absent for a restaurant table reservation, which has no package to buy. */
  packageId?: string;
  date: string;
  dateEnd?: string;
  time?: string;
  adults: number;
  children: number;
  infants: number;
  hours?: number;
  nights?: number;
  groups?: number;
  /** Stays only: tents or lodges booked (camps), each priced per night. */
  rooms?: number;
  transport: boolean;
  transportOutside?: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  transportAddress?: string;
  paymentMethod: "arrival";
  notes?: string;
  couponCode?: string;
  total: number;
  addons?: Array<{ variantId: string; quantity: number }>;
  /**
   * Several treatments in one booking, each with its own quantity. Spas only —
   * the server ignores it for any other category and books `packageId` alone.
   */
  packs?: Array<{ packageId: string; quantity: number }>;
  /** Any SUPPORTED_LANGS locale — picks the language of the confirmation email. */
  lang?: Lang;
  /** Present only when the guest added a transfer to this booking. */
  transfer?: TransferBookingInput;
  /** A women-only trip leaving from several cities: the one she picked. */
  tripDeparture?: string;
}

export function createCheckoutApi(api: ApiClient) {
  return {
    submit: async (payload: CheckoutPayload) => {
      const apiPayload = {
        activitySlug: payload.activitySlug,
        packageId: payload.packageId,
        adults: payload.adults,
        children: payload.children,
        infants: payload.infants,
        date: payload.date,
        dateEnd: payload.dateEnd,
        time: payload.time,
        hours: payload.hours,
        nights: payload.nights,
        groups: payload.groups,
        rooms: payload.rooms,
        paymentMethod: payload.paymentMethod,
        total: payload.total,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        phone: payload.phone,
        transport: payload.transport,
        transportOutside: payload.transportOutside ?? false,
        transportAddress: payload.transportAddress,
        notes: payload.notes,
        addons: payload.addons,
        packs: payload.packs,
        lang: payload.lang,
        transfer: payload.transfer,
        tripDeparture: payload.tripDeparture,
        source: deriveSource(),
        gclid: readGclid(),
        trafficSource: readTrafficSource(),
        aiAssistant: readAiAssistant(),
        adCampaign: readAdCampaign(),
        ref: readReferral(),
        ...readMetaAttribution(),
      };
      const result = await api.bookings.checkout.post(apiPayload);
      if (result.error) {
        throw result.error;
      }

      const body = result.data;
      if (!body || !("data" in body) || !body.data) {
        throw new Error("Booking failed. Please try again.");
      }

      return body.data;
    },

    applyCoupon: async (code: string, activityId: string) =>
      unwrap(await api.coupons.apply.post({ code, activityId })),
  };
}

import { useMemo } from "react";
import { useBookingContext } from "../provider";
import type { Lang } from "@majourneys/shared";

export function useCheckoutApi() {
  const { api } = useBookingContext();
  return useMemo(() => createCheckoutApi(api), [api]);
}
